import { useParams, Link, useNavigate } from 'react-router-dom';
import { TREATMENTS, CLINIC_INFO } from '../data';
import { ChevronLeft, Calendar, Clock, CheckCircle2, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect } from 'react';

export default function TreatmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const treatment = TREATMENTS.find((t) => t.id === id);

  useEffect(() => {
    if (treatment) {
      document.title = `CDH Sports- og Wellness Massage - ${treatment.title}`;
    }
  }, [treatment]);

  if (!treatment) {
    return (
      <div className="pt-40 pb-20 container mx-auto px-4 text-center">
        <h1 className="text-3xl font-bold text-blue-900 mb-6">Behandlingen blev ikke fundet</h1>
        <Link to="/behandlinger" className="text-blue-600 font-bold hover:underline">
          Gå tilbage til alle behandlinger
        </Link>
      </div>
    );
  }

  if (treatment.id === 'ir-sauna') {
    return (
      <div className="pt-24 md:pt-32">
        <div className="container mx-auto px-4 pt-8 md:pt-10 lg:pt-16 pb-12">
          <Link 
            to="/behandlinger" 
            className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors mb-8 group"
          >
            <ChevronLeft className="w-5 h-5 mr-1 transition-transform group-hover:-translate-x-1" />
            Tilbage til behandlinger
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column: Main Content */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-12">
              <div>
                <h1 className="text-3xl md:text-5xl font-bold text-blue-900 mb-6 leading-tight">
                  {treatment.title}
                </h1>
                <p className="text-lg md:text-xl text-blue-800 font-medium mb-10 leading-relaxed">
                  Her har du mulighed for at booke tid til en tur i en infrarød sauna her i klinikken – Enten som en kombination med en massage eller ”bare” sauna.
                </p>
              </div>

              {/* Section: Hvad er en Infrarød Sauna? */}
              <div className="bg-white rounded-3xl border border-blue-50 p-8 md:p-10 shadow-sm space-y-6">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-900">
                  Hvad er en Infrarød Sauna?
                </h2>
                
                {/* Image directly under the heading */}
                <div className="overflow-hidden rounded-2xl shadow-lg bg-gray-50 my-6 flex justify-center">
                  <img 
                    src={treatment.image} 
                    alt="Infrarød Sauna" 
                    className="w-full h-auto max-h-[500px] object-contain hover:scale-102 transition-transform duration-700"
                  />
                </div>

                <div className="space-y-4 text-gray-600 leading-relaxed text-sm sm:text-base">
                  <p>En infrarød Sauna er i sagens natur "bare" en sauna, men som på nogle punkter er meget forskellig fra den tradionelle sauna.</p>
                  <ul className="list-disc pl-5 space-y-2 mt-2">
                    <li>I en infrarød sauna bliver du varmet op af infrarød strålevarme (direkte varme), som trænger ind i kroppen (ca 4 cm) og giver en fornemmelse af at blive varm "indefra".</li>
                    <li>I en traditionel sauna bliver du varmet op at den varme luft (Indirekte varme) i saunaen, som giver en mere overfladisk varme.</li>
                    <li>En traditionel sauna er ofte 90-100 grader varm og har en meget høj luftfugtighed.</li>
                    <li>Ir sauna er typisk kun mellem 45-55 grader og luften er tør.</li>
                  </ul>
                  <p className="mt-4">Så har du ligesom jeg svært ved at få luft/trække vejret i en traditionel sauna, så er ir sauna måske en mulighed for dig.</p>
                </div>
              </div>

              {/* Section: Alternativ behandling med Infrarød Sauna */}
              <div className="bg-white rounded-3xl border border-blue-50 p-8 md:p-10 shadow-sm space-y-6">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-900">
                  Alternativ behandling med Infrarød Sauna
                </h2>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                  Her er nogle af de mange fordele der kan være ved at bruge IR Sauna, som alternativ behandling.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {treatment.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-center text-gray-700 bg-blue-50/30 p-4 rounded-xl border border-blue-50/50">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3 shrink-0" />
                      <span className="text-[13px] sm:text-sm font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
                {treatment.benefitsFooter && (
                  <div className="pt-4 border-t border-blue-50/50">
                    <p className="text-base sm:text-lg font-bold text-blue-900 tracking-wide">
                      {treatment.benefitsFooter}
                    </p>
                  </div>
                )}
              </div>

              {/* Section: Bestilling af tid. */}
              <div className="bg-white rounded-3xl border border-blue-50 p-8 md:p-10 shadow-sm space-y-4">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-900">
                  Bestilling af tid.
                </h2>
                <div className="space-y-3 text-gray-600 leading-relaxed text-sm sm:text-base">
                  <p>Når du booker tid til en tur i den infrarøde sauna, kan du booke enten 30, 45 eller 60 min.</p>
                  <p>Dette kan gøre via online booking</p>
                  <p>Du kan vælge at bruge den alene, eller sammen med op til 2 andre på samme tid (mod merpris)</p>
                </div>
              </div>

              {/* Section: Håndklæder? & Med eller uden tøj? */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-emerald-50/50 p-8 rounded-3xl border border-emerald-100/50 space-y-3">
                  <h3 className="text-xl font-bold text-blue-900 flex items-center">
                    <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3 text-emerald-600 font-bold text-sm">✓</span>
                    Håndklæder?
                  </h3>
                  <div className="text-gray-600 leading-relaxed text-sm sm:text-base space-y-2">
                    <p>Da du SKAL sidde/ligge på et håndklæde, får du udleveret 2 store håndklæder til låns.</p>
                    <p>Det er en del af prisen, så det behøver du ikke selv at medbringe</p>
                  </div>
                </div>

                <div className="bg-blue-50/50 p-8 rounded-3xl border border-blue-100/50 space-y-3">
                  <h3 className="text-xl font-bold text-blue-900 flex items-center">
                    <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-blue-600 font-bold text-sm">?</span>
                    Med eller uden tøj?
                  </h3>
                  <div className="text-gray-600 leading-relaxed text-sm sm:text-base space-y-2">
                    <p>Om du vælger at sidde i badetøj, undertøj eller helt uden tøj er op til dig selv.</p>
                    <p>Hvis du vælger at sidde i badetøj eller undertøj vil jeg anbefale at du har skiftetøj med til bagefter. Man kan komme til at svede en del.</p>
                  </div>
                </div>
              </div>

              {/* Section: Hvad taler imod Infrarød Sauna? */}
              <div className="bg-rose-50/30 p-8 md:p-10 rounded-[2rem] border border-rose-100/50 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-rose-950 mb-2 flex items-center">
                    <span className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center mr-3 text-rose-600 font-bold text-sm">!</span>
                    Hvad taler imod Infrarød Sauna?
                  </h3>
                  <p className="text-rose-900/80 font-medium text-sm sm:text-base">
                    Selvom der er mange fordele ved at benytte sig af en Infrarød Sauna, så er der også ting, der taler imod.
                  </p>
                </div>

                <div className="border-t border-rose-100/50 pt-4">
                  <p className="text-rose-950 font-bold mb-4 text-sm sm:text-base">Anvendelse frarådes ved</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {treatment.contraindications.list.map((item, idx) => (
                      <div key={idx} className="bg-white px-4 py-3 rounded-xl border border-rose-100 text-rose-900 text-xs sm:text-sm font-medium flex items-center">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full mr-2 shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-xs sm:text-sm text-rose-900/70 space-y-3 border-t border-rose-100/50 pt-6">
                  <p className="leading-relaxed">Konsulter altid egen læge, hvis du er i tvivl eller ved indtagelse af receptpligtig medicin.</p>
                  <p className="leading-relaxed">Gå aldrig i Infrarød Sauna, hvis du har indtaget alkohol eller andre rusmidler, da dette kan medføre bevidstløshed.</p>
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Pricing & CTA */}
            <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-32 space-y-8">
              <div className="bg-blue-50 p-10 rounded-[2rem] border border-blue-100">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">Pris: IR Sauna</h3>

                <div className="space-y-6">
                  {/* Enkelt person */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-blue-800/70 mb-2">
                      Priser ved en enkelt person
                    </h4>
                    <div className="divide-y divide-blue-100/50">
                      <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-gray-700">30 min</span>
                        </div>
                        <span className="text-lg sm:text-xl font-bold text-blue-900">150,-</span>
                      </div>
                      <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-gray-700">45 min</span>
                        </div>
                        <span className="text-lg sm:text-xl font-bold text-blue-900">200,-</span>
                      </div>
                      <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-gray-700">60 min</span>
                        </div>
                        <span className="text-lg sm:text-xl font-bold text-blue-900">250,-</span>
                      </div>
                    </div>
                  </div>

                  {/* Tillæg */}
                  <div className="pt-4 border-t border-blue-100">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-blue-800/70 mb-2">
                      Tillæg for 2. og 3. person (Pr. person)
                    </h4>
                    <div className="flex justify-between items-center py-4">
                      <div className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                        <span className="text-sm sm:text-base font-medium text-gray-700">Ekstra person</span>
                      </div>
                      <span className="text-lg sm:text-xl font-bold text-blue-900">75,-</span>
                    </div>
                  </div>

                  {/* Kombination */}
                  <div className="pt-4 border-t border-blue-100">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-blue-800/70 mb-2">
                      Tilvælger du IR sauna i forbindelse med en massage er prisen
                    </h4>
                    <div className="divide-y divide-blue-100/50">
                      <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-gray-700">30 min</span>
                        </div>
                        <span className="text-lg sm:text-xl font-bold text-blue-900">100,-</span>
                      </div>
                      <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-gray-700">45 min</span>
                        </div>
                        <span className="text-lg sm:text-xl font-bold text-blue-900">150,-</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-6 text-[11px] sm:text-xs text-gray-400 italic">
                  Alle priser er inkl. moms og lån af håndklæder.
                </p>

                <div className="pt-6 border-t border-blue-100 mt-6 space-y-4">
                  <Link
                    to="/bestil-tid"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-2xl font-bold text-base shadow-xl hover:shadow-2xl transition-all flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Bestil tid nu
                  </Link>
                  <a
                    href={`tel:${CLINIC_INFO.phone}`}
                    className="w-full bg-white border-2 border-blue-100 text-blue-700 px-6 py-4 rounded-2xl font-bold text-base hover:bg-blue-50 transition-all flex items-center justify-center"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Ring og hør nærmere
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-32">
      <div className="container mx-auto px-4 pt-8 md:pt-10 lg:pt-16 pb-12">
        <Link 
          to="/behandlinger" 
          className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors mb-8 group"
        >
          <ChevronLeft className="w-5 h-5 mr-1 transition-transform group-hover:-translate-x-1" />
          Tilbage til behandlinger
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Column: Image & Info */}
          <div className="space-y-12">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="aspect-video lg:aspect-square rounded-[3rem] overflow-hidden shadow-2xl"
            >
              <img 
                src={treatment.image} 
                alt={treatment.title} 
                className="w-full h-full object-cover"
              />
            </motion.div>

            <div className="bg-blue-50 p-10 rounded-[2rem] border border-blue-100">
              <h3 className="text-2xl font-bold text-blue-900 mb-6">Priser & Varighed</h3>
              <div className="space-y-6">
                {treatment.pricing.map((p, idx) => (
                  <div key={idx} className="flex justify-between items-center py-5 border-b border-blue-100 last:border-0">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-emerald-500 mr-3" />
                      <span className="text-sm sm:text-base font-medium text-gray-700">{p.duration}</span>
                    </div>
                    <span className="text-lg sm:text-xl font-bold text-blue-900">{p.price}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-[11px] sm:text-xs text-gray-400 italic">
                Alle priser er inkl. moms. Tiden er inkl. af- og påklædning.
              </p>
            </div>
          </div>

          {/* Right Column: Text & CTA */}
          <div className="lg:sticky lg:top-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-3xl md:text-5xl font-bold text-blue-900 mb-6 leading-tight">
                {treatment.title}
              </h1>
              <div className="prose prose-sm sm:prose-base md:prose-lg text-gray-600 mb-12">
                <p className="text-lg md:text-xl text-blue-800 font-medium mb-8 leading-relaxed">
                  {treatment.description}
                </p>
                <div className="space-y-6 text-sm sm:text-base leading-relaxed">
                  {treatment.fullDescription.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>

              <div className="mb-12">
                <h4 className="text-xl font-bold text-blue-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </span>
                  Hvorfor vælge denne behandling?
                </h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {treatment.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center text-gray-700 bg-white p-4 rounded-xl shadow-sm border border-blue-50">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3 shrink-0" />
                      <span className="text-[13px] sm:text-sm font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/bestil-tid"
                  className="flex-grow bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 lg:px-8 lg:py-5 rounded-2xl font-bold text-base lg:text-lg shadow-xl hover:shadow-2xl transition-all flex items-center justify-center"
                >
                  <Calendar className="w-6 h-6 mr-2" />
                  Bestil tid nu
                </Link>
                <a
                  href={`tel:${CLINIC_INFO.phone}`}
                  className="bg-white border-2 border-blue-100 text-blue-700 px-6 py-3.5 lg:px-8 lg:py-5 rounded-2xl font-bold text-base lg:text-lg hover:bg-blue-50 transition-all flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Ring
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Custom sections for IR Sauna or other treatments with extra info */}
        {treatment.towels && (
          <div className="mt-16 pt-16 border-t border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="bg-emerald-50/50 p-8 md:p-10 rounded-[2rem] border border-emerald-100/50">
                <h3 className="text-2xl font-bold text-blue-900 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3 text-emerald-600 font-bold text-sm">✓</span>
                  {treatment.towels.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{treatment.towels.text}</p>
              </div>
              
              {treatment.clothing && (
                <div className="bg-blue-50/50 p-8 md:p-10 rounded-[2rem] border border-blue-100/50">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-blue-600 font-bold text-sm">?</span>
                    {treatment.clothing.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{treatment.clothing.text}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {treatment.contraindications && (
          <div className="mt-12 bg-rose-50/30 p-8 md:p-12 rounded-[3rem] border border-rose-100/50">
            <h3 className="text-2xl font-bold text-rose-950 mb-2 flex items-center">
              <span className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center mr-3 text-rose-600 font-bold text-sm">!</span>
              {treatment.contraindications.title}
            </h3>
            <p className="text-rose-900/80 font-medium mb-6 text-sm sm:text-base">{treatment.contraindications.subtitle}</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
              {treatment.contraindications.list.map((item, idx) => (
                <div key={idx} className="bg-white px-4 py-3 rounded-xl border border-rose-100 text-rose-900 text-sm font-medium flex items-center">
                  <span className="w-1.5 h-1.5 bg-rose-500 rounded-full mr-2 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            
            <div className="text-xs sm:text-sm text-rose-900/70 space-y-4 border-t border-rose-100/50 pt-6">
              {treatment.contraindications.warning.split('\n\n').map((para, idx) => (
                <p key={idx} className="leading-relaxed">{para}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
